import axios from "axios";
import { useEffect, useMemo } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useImmer } from "use-immer";

import { APP_BASE_URL, GET_ALL_CUSTOMER } from "../../Common/EndPoints";
import DataTable from "../DataGrid/DataGrid";
import UpdateGeoFencing from "./UpdateGeoFencing";

const customerFixedColumn: GridColDef[] = [
  {
    field: "zoneCode",
    headerName: "Zone",
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "divisionCode",
    headerName: "Division",
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "stnCode",
    headerName: "Station",
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "isResticted",
    headerName: "Restriction",
    minWidth: 110,
    hideable: true,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <>
          {params.row?.additionalDetail?.geotagging_restricted ? "Restricted" : "Unrestricted"}{" "}
          {params.row?.additionalDetail?.geotagging_restricted &&
            `,${params.row?.additionalDetail?.geotagging_distance || ""}m`}
        </>
      );
    },
  },
];

const fetchAllCustomer = async (signal: AbortSignal | undefined, cb: (customerList: any[]) => void) => {
  try {
    let url = APP_BASE_URL + GET_ALL_CUSTOMER;
    const res = await axios.get(url, { signal });
    let data = res.data?.data || [];
    const customerList = data.map((customer: any) => ({
      ...customer,
      id: customer.customerId,
      additionalDetail: customer.additionalDetail ? JSON.parse(customer.additionalDetail) : null,
    }));
    cb(customerList);
  } catch (error) {
    // console.log("Error", error);
  }
};

type State = {
  customerList: any[];
  customerData: any;
  loading: boolean;
  showEditModal: boolean;
};

function GeoFencing() {
  const [state, setState] = useImmer<State>({
    customerList: [],
    customerData: null,
    loading: false,
    showEditModal: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState((state) => {
      state.loading = true;
    });
    fetchAllCustomer(signal, (customerList: any[]) => {
      setState((state) => {
        state.customerList = customerList;
        state.loading = false;
      });
    });

    return () => {
      controller.abort();
    };
  }, []);

  const customerColumn: GridColDef[] = useMemo(() => {
    return [
      ...customerFixedColumn,
      {
        field: "editActions",
        type: "actions",
        headerName: "Action",
        filterable: false,
        sortable: false,
        hideable: true,
        flex: 1,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          return (
            <>
              <Tooltip placement="bottom" title={<div className="tooltip--title">Edit</div>} disableInteractive>
                <IconButton
                  aria-label="Edit"
                  size="small"
                  onClick={() => {
                    setState((state) => {
                      state.showEditModal = true;
                      state.customerData = params.row;
                    });
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    ];
  }, []);

  return (
    <div className="users">
      <DataTable
        tableInfo={{
          id: `customer-table`,
          label: `Customer`,
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={state.loading}
        rows={state?.customerList ?? []}
        columns={customerColumn}
      />

      {state.showEditModal && (
        <UpdateGeoFencing
          initialData={state.customerData}
          onClose={(byCross?: boolean) => {
            if (!byCross) {
              fetchAllCustomer(undefined, (customerList: any[]) => {
                setState((state) => {
                  state.customerList = customerList;
                });
              });
            }
            setState((state) => {
              state.showEditModal = false;
              state.customerData = null;
            });
          }}
        />
      )}
    </div>
  );
}

export default GeoFencing;
